import { create, type StateCreator } from 'zustand'
import { devtools, persist, subscribeWithSelector, type PersistOptions } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

/**
 * Factory for client-state Zustand stores that need to survive a page reload.
 *
 * Composes (outer → inner):
 *   devtools (DEV-disabled in prod) → persist → subscribeWithSelector → immer
 *
 * Use this for **non-credential** UI / preference state — theme, language,
 * sidebar collapsed/expanded, etc. NEVER use it for tokens, permissions,
 * userId, or anything CLAUDE.md hard-rule #5 forbids in localStorage.
 *
 * The `partialize` option in PersistOptions is the **mandatory escape hatch**
 * for stores that mix sensitive and non-sensitive state — pass an explicit
 * field selector so only the safe subset reaches localStorage.
 *
 * @example
 *   export const useThemeStore = createPersistedStore<ThemeState>(
 *     (set) => ({
 *       theme: defaultTheme,
 *       setTheme: (t) => set((s) => { s.theme = t }),
 *     }),
 *     { name: 'glasspro:theme', version: 1 },
 *   )
 */
export function createPersistedStore<T, Persisted = T>(
  initializer: StateCreator<T, [['zustand/immer', never]], [], T>,
  persistOptions: PersistOptions<T, Persisted> & { name: string },
) {
  return create<T>()(
    devtools(
      persist<
        T,
        [],
        [['zustand/subscribeWithSelector', never], ['zustand/immer', never]],
        Persisted
      >(subscribeWithSelector(immer<T>(initializer)), persistOptions),
      { name: persistOptions.name, enabled: import.meta.env.DEV },
    ),
  )
}
